// src/components/PatientFollowUp.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  // Tratamientos (TreatmentCourse)
  fetchPatientCases,
  createPatientCase,
  updatePatientCase,
  deletePatientCase,
  // Cirugías
  fetchSurgeries,
  createSurgery,
  updateSurgery,
  deleteSurgery,
  // Hitos (PatientJourneyItem)
  fetchPatientJourneyItems,
  createPatientJourneyItem,
  updatePatientJourneyItem,
  deletePatientJourneyItem,
} from "../features/clinic/clinicThunks.js";
import { fetchDoctors } from "../features/appointments/calendaryThunks.js";

import {
  Activity,
  CalendarDays,
  CheckCircle2,
  Clock,
  AlertCircle,
  Plus,
  Pencil,
  X,
  User as UserIcon,
} from "lucide-react";

import "../styles/patients/patientfollowup.css";

/* =========================
   Helpers de fecha
========================= */

const toDateInput = (isoOrDate) => {
  if (!isoOrDate) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(isoOrDate)) return isoOrDate;
  const dateObj = new Date(isoOrDate);
  if (Number.isNaN(dateObj.getTime())) return "";
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const niceDate = (isoOrDate) => {
  if (!isoOrDate) return "—";
  if (/^\d{4}-\d{2}-\d{2}$/.test(isoOrDate)) {
    const [year, month, day] = isoOrDate.split("-");
    return `${day}/${month}/${year}`;
  }
  const dateObj = new Date(isoOrDate);
  return Number.isNaN(dateObj.getTime()) ? "—" : dateObj.toLocaleDateString();
};

const fmtDateTimeLocal = (iso) => {
  if (!iso) return "";
  const dateObj = new Date(iso);
  if (Number.isNaN(dateObj.getTime())) return "";
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  const hours = String(dateObj.getHours()).padStart(2, "0");
  const minutes = String(dateObj.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const toIsoFromLocalInput = (value) => {
  if (!value) return null;
  const dateObj = new Date(value);
  if (Number.isNaN(dateObj.getTime())) return null;
  return dateObj.toISOString();
};

const todayDateInput = () => {
  const dateObj = new Date();
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const JOURNEY_PHASES = [
  { key: "consultation", label: "Consulta" },
  { key: "preop", label: "Preoperatorio" },
  { key: "surgery", label: "Cirugía" },
  { key: "postop", label: "Postoperatorio" },
  { key: "followup", label: "Seguimiento" },
];

const JOURNEY_STATUS_OPTIONS = [
  { value: "planned", label: "Planificado" },
  { value: "done", label: "Hecho" },
  { value: "cancelled", label: "Cancelado" },
];

const TREATMENT_STATUS_OPTIONS = [
  { value: "active", label: "Activo" },
  { value: "completed", label: "Completado" },
  { value: "cancelled", label: "Cancelado" },
  { value: "archived", label: "Archivado" },
];

/* =========================
   Componente
========================= */

const PatientFollowUp = () => {
  const { id: patientId } = useParams();
  const dispatch = useDispatch();

  // Redux
  const clinicState = useSelector((state) => state.clinic);
  const calendaryState = useSelector((state) => state.calendary);
  const authState = useSelector((state) => state.auth);

  const patientData = clinicState?.patientData || null;
  const treatmentsState = clinicState?.patientCases;
  const surgeriesState = clinicState?.surgeries;
  const journeyItemsState = clinicState?.patientJourneyItems;

  const loading = clinicState?.loading || false;
  const error = clinicState?.error || null;

  const doctorsState = calendaryState?.doctors;
  const user = authState?.user || null;

  const treatments = Array.isArray(treatmentsState) ? treatmentsState : [];
  const surgeries = Array.isArray(surgeriesState) ? surgeriesState : [];
  const patientJourneyItems = Array.isArray(journeyItemsState) ? journeyItemsState : [];

  const doctors = useMemo(() => {
    if (Array.isArray(doctorsState)) return doctorsState;
    if (Array.isArray(doctorsState?.results)) return doctorsState.results;
    return [];
  }, [doctorsState]);

  // Guards
  if (!user) return <div>Cargando sesión…</div>;
  if (loading && !patientData) return <div>Cargando…</div>;
  if (error && !patientData) return <div>Error: {error}</div>;
  if (!patientData) return null;

  const canEdit = user.role === "doctor" || user.role === "receptionist" || user.role === "admin";

  /* =========================
     Selección de tratamiento
  ========================= */

  const [selectedTreatmentId, setSelectedTreatmentId] = useState(null);

  useEffect(() => {
    if (!patientId) return;
    dispatch(fetchPatientCases({ patientId }));
    dispatch(fetchSurgeries(patientId));
    dispatch(fetchDoctors());
  }, [dispatch, patientId]);

  // Selección automática: activo o el más reciente
  useEffect(() => {
    if (treatments.length === 0) {
      setSelectedTreatmentId(null);
      return;
    }

    if (!selectedTreatmentId) {
      const active = treatments.find((t) => t.status === "active") || treatments[0];
      setSelectedTreatmentId(active?.id ?? null);
      return;
    }

    const exists = treatments.some((t) => String(t.id) === String(selectedTreatmentId));
    if (!exists) {
      const active = treatments.find((t) => t.status === "active") || treatments[0];
      setSelectedTreatmentId(active?.id ?? null);
    }
  }, [treatments, selectedTreatmentId]);

  const selectedTreatment = useMemo(() => {
    if (!selectedTreatmentId) return null;
    return treatments.find((t) => String(t.id) === String(selectedTreatmentId)) || null;
  }, [treatments, selectedTreatmentId]);

  // Cargar hitos del tratamiento seleccionado
  useEffect(() => {
    if (!patientId || !selectedTreatmentId) return;
    dispatch(fetchPatientJourneyItems({ patientId, courseId: selectedTreatmentId }));
  }, [dispatch, patientId, selectedTreatmentId]);

  /* =========================
     Helpers de UI
  ========================= */

  const getDoctorLabel = (doctor) => {
    if (!doctor) return "";
    const firstName = doctor.first_name || "";
    const lastName = doctor.last_name || "";
    const fullName = `${firstName} ${lastName}`.trim();
    if (fullName) return `Dr. ${fullName}`;
    if (doctor.username) return `Dr. ${doctor.username}`;
    return `Doctor #${doctor.id}`;
  };

  const phaseLabel = (phaseKey) => {
    return JOURNEY_PHASES.find((p) => p.key === phaseKey)?.label || phaseKey || "—";
  };

  const statusLabel = (statusValue) => {
    return JOURNEY_STATUS_OPTIONS.find((s) => s.value === statusValue)?.label || statusValue || "—";
  };

  const treatmentStatusLabel = (statusValue) => {
    return TREATMENT_STATUS_OPTIONS.find((s) => s.value === statusValue)?.label || statusValue || "—";
  };

  const surgeriesByTreatmentId = useMemo(() => {
    const map = new Map();
    for (const surgery of surgeries) {
      const courseId = surgery?.course;
      if (courseId == null) continue;
      const key = String(courseId);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(surgery);
    }

    for (const [key, list] of map.entries()) {
      list.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
      map.set(key, list);
    }

    return map;
  }, [surgeries]);

  const getTreatmentSurgeries = (treatmentId) => {
    return surgeriesByTreatmentId.get(String(treatmentId)) || [];
  };

  /* =========================
     Timeline (hitos)
     ✅ Nuevo arriba, antiguo abajo (para que "se vaya al scroll")
  ========================= */

  const journeyTimeline = useMemo(() => {
    const itemsCopy = [...patientJourneyItems];
    itemsCopy.sort((a, b) => {
      const timeA = new Date(a.event_datetime || a.created_at).getTime();
      const timeB = new Date(b.event_datetime || b.created_at).getTime();
      return timeB - timeA;
    });
    return itemsCopy;
  }, [patientJourneyItems]);

  /* =========================
     Modal UX: ESC + bloquear scroll
  ========================= */

  const [createTreatmentOpen, setCreateTreatmentOpen] = useState(false);
  const [editTreatmentOpen, setEditTreatmentOpen] = useState(false);
  const [deleteTreatmentOpen, setDeleteTreatmentOpen] = useState(false);

  const [createSurgeryOpen, setCreateSurgeryOpen] = useState(false);
  const [deleteSurgeryOpen, setDeleteSurgeryOpen] = useState(false);

  const [editJourneyOpen, setEditJourneyOpen] = useState(false);
  const [deleteJourneyOpen, setDeleteJourneyOpen] = useState(false);

  /* =========================
     Tratamientos: crear / editar / eliminar
  ========================= */

  const [createTreatmentSaving, setCreateTreatmentSaving] = useState(false);
  const [createTreatmentError, setCreateTreatmentError] = useState(null);
  const [createTreatmentForm, setCreateTreatmentForm] = useState({
    start_date: todayDateInput(),
    status: "active",
    description: "",
  });

  const [treatmentToEdit, setTreatmentToEdit] = useState(null);
  const [editTreatmentSaving, setEditTreatmentSaving] = useState(false);
  const [editTreatmentError, setEditTreatmentError] = useState(null);
  const [editTreatmentForm, setEditTreatmentForm] = useState({
    start_date: "",
    end_date: "",
    status: "active",
    description: "",
  });

  const [treatmentToDelete, setTreatmentToDelete] = useState(null);
  const [deleteTreatmentSaving, setDeleteTreatmentSaving] = useState(false);
  const [deleteTreatmentError, setDeleteTreatmentError] = useState(null);

  const openCreateTreatmentModal = () => {
    if (!canEdit) return;
    setCreateTreatmentError(null);
    setCreateTreatmentForm({ start_date: todayDateInput(), status: "active", description: "" });
    setCreateTreatmentOpen(true);
  };

  const closeCreateTreatmentModal = () => {
    if (createTreatmentSaving) return;
    setCreateTreatmentOpen(false);
    setCreateTreatmentError(null);
  };

  const handleCreateTreatmentChange = (event) => {
    const { name, value } = event.target;
    setCreateTreatmentForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveCreateTreatment = async () => {
    if (!canEdit || !patientId) return;

    setCreateTreatmentError(null);
    setCreateTreatmentSaving(true);

    const startDate = (createTreatmentForm.start_date || "").trim() || todayDateInput();
    const payload = {
      title: `Tratamiento - ${startDate}`,
      description: (createTreatmentForm.description || "").trim(),
      status: createTreatmentForm.status || "active",
      current_phase: "evaluation",
      start_date: startDate,
    };

    const result = await dispatch(createPatientCase({ patientId, data: payload }));
    setCreateTreatmentSaving(false);

    if (createPatientCase.fulfilled.match(result)) {
      const created = result.payload;
      closeCreateTreatmentModal();
      if (created?.id) setSelectedTreatmentId(created.id);
      return;
    }

    setCreateTreatmentError(result.payload || "No se pudo crear el tratamiento.");
  };

  const openEditTreatmentModal = (treatment) => {
    if (!canEdit) return;
    setEditTreatmentError(null);
    setTreatmentToEdit(treatment);

    setEditTreatmentForm({
      start_date: toDateInput(treatment?.start_date || ""),
      end_date: toDateInput(treatment?.end_date || ""),
      status: treatment?.status || "active",
      description: treatment?.description || "",
    });

    setEditTreatmentOpen(true);
  };

  const closeEditTreatmentModal = () => {
    if (editTreatmentSaving) return;
    setEditTreatmentOpen(false);
    setTreatmentToEdit(null);
    setEditTreatmentError(null);
  };

  const handleEditTreatmentChange = (event) => {
    const { name, value } = event.target;
    setEditTreatmentForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveEditTreatment = async () => {
    if (!canEdit || !treatmentToEdit?.id) return;

    setEditTreatmentError(null);
    setEditTreatmentSaving(true);

    const payload = {
      start_date: (editTreatmentForm.start_date || "").trim() || null,
      end_date: (editTreatmentForm.end_date || "").trim() || null,
      status: editTreatmentForm.status,
      description: (editTreatmentForm.description || "").trim(),
    };

    const result = await dispatch(updatePatientCase({ id: treatmentToEdit.id, data: payload }));
    setEditTreatmentSaving(false);

    if (updatePatientCase.fulfilled.match(result)) {
      closeEditTreatmentModal();
      return;
    }

    setEditTreatmentError(result.payload || "No se pudo actualizar el tratamiento.");
  };

  const openDeleteTreatmentModal = (treatment) => {
    if (!canEdit) return;
    setDeleteTreatmentError(null);
    setTreatmentToDelete(treatment);
    setDeleteTreatmentOpen(true);
  };

  const closeDeleteTreatmentModal = () => {
    if (deleteTreatmentSaving) return;
    setDeleteTreatmentOpen(false);
    setTreatmentToDelete(null);
    setDeleteTreatmentError(null);
  };

  const confirmDeleteTreatment = async () => {
    if (!canEdit || !treatmentToDelete?.id) return;

    setDeleteTreatmentError(null);
    setDeleteTreatmentSaving(true);

    const result = await dispatch(deletePatientCase(treatmentToDelete.id));
    setDeleteTreatmentSaving(false);

    if (deletePatientCase.fulfilled.match(result)) {
      closeDeleteTreatmentModal();
      return;
    }

    setDeleteTreatmentError(result.payload || "No se pudo eliminar el tratamiento.");
  };

  /* =========================
     Cirugías: crear / editar / eliminar (modal)
  ========================= */

  const [surgeryModalSaving, setSurgeryModalSaving] = useState(false);
  const [surgeryModalError, setSurgeryModalError] = useState(null);
  const [surgeryCourseId, setSurgeryCourseId] = useState(null);
  const [surgeryToEdit, setSurgeryToEdit] = useState(null);
  const [surgeryForm, setSurgeryForm] = useState({
    procedure_name: "",
    date: "",
    surgeon: "",
    notes: "",
  });

  const [surgeryToDelete, setSurgeryToDelete] = useState(null);
  const [deleteSurgerySaving, setDeleteSurgerySaving] = useState(false);
  const [deleteSurgeryError, setDeleteSurgeryError] = useState(null);

  const openCreateSurgeryModal = (courseId) => {
    if (!canEdit) return;
    setSurgeryModalError(null);
    setSurgeryToEdit(null);
    setSurgeryCourseId(courseId);
    setSurgeryForm({ procedure_name: "", date: todayDateInput(), surgeon: "", notes: "" });
    setCreateSurgeryOpen(true);
  };

  const openEditSurgeryModal = (courseId, surgery) => {
    if (!canEdit) return;
    setSurgeryModalError(null);
    setSurgeryToEdit(surgery);
    setSurgeryCourseId(courseId);

    setSurgeryForm({
      procedure_name: surgery?.procedure_name || "",
      date: toDateInput(surgery?.date || ""),
      surgeon: surgery?.surgeon != null ? String(surgery.surgeon) : "",
      notes: surgery?.notes || "",
    });

    setCreateSurgeryOpen(true);
  };

  const closeSurgeryModal = () => {
    if (surgeryModalSaving) return;
    setCreateSurgeryOpen(false);
    setSurgeryToEdit(null);
    setSurgeryCourseId(null);
    setSurgeryModalError(null);
  };

  const handleSurgeryFormChange = (event) => {
    const { name, value } = event.target;
    setSurgeryForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveSurgeryModal = async () => {
    if (!canEdit || !patientId || !surgeryCourseId) return;

    const procedureName = (surgeryForm.procedure_name || "").trim();
    const surgeryDate = (surgeryForm.date || "").trim();

    if (!procedureName || !surgeryDate) {
      setSurgeryModalError("Procedimiento y fecha son obligatorios.");
      return;
    }

    setSurgeryModalError(null);
    setSurgeryModalSaving(true);

    const payload = {
      course: Number(surgeryCourseId),
      procedure_name: procedureName,
      date: surgeryDate,
      surgeon: surgeryForm.surgeon ? Number(surgeryForm.surgeon) : null,
      notes: (surgeryForm.notes || "").trim(),
    };

    if (!surgeryToEdit?.id) {
      const result = await dispatch(createSurgery({ patientId, data: payload }));
      setSurgeryModalSaving(false);

      if (createSurgery.fulfilled.match(result)) {
        closeSurgeryModal();
        return;
      }

      setSurgeryModalError(result.payload || "No se pudo crear la cirugía.");
      return;
    }

    const result = await dispatch(updateSurgery({ id: surgeryToEdit.id, data: payload }));
    setSurgeryModalSaving(false);

    if (updateSurgery.fulfilled.match(result)) {
      closeSurgeryModal();
      return;
    }

    setSurgeryModalError(result.payload || "No se pudo actualizar la cirugía.");
  };

  const openDeleteSurgeryModal = (surgery) => {
    if (!canEdit) return;
    setDeleteSurgeryError(null);
    setSurgeryToDelete(surgery);
    setDeleteSurgeryOpen(true);
  };

  const closeDeleteSurgeryModal = () => {
    if (deleteSurgerySaving) return;
    setDeleteSurgeryOpen(false);
    setSurgeryToDelete(null);
    setDeleteSurgeryError(null);
  };

  const confirmDeleteSurgery = async () => {
    if (!canEdit || !surgeryToDelete?.id) return;

    setDeleteSurgeryError(null);
    setDeleteSurgerySaving(true);

    const result = await dispatch(deleteSurgery(surgeryToDelete.id));
    setDeleteSurgerySaving(false);

    if (deleteSurgery.fulfilled.match(result)) {
      closeDeleteSurgeryModal();
      return;
    }

    setDeleteSurgeryError(result.payload || "No se pudo eliminar la cirugía.");
  };

  /* =========================
     Hitos: crear + modales editar/eliminar
  ========================= */

  const [journeySaving, setJourneySaving] = useState(false);
  const [journeyForm, setJourneyForm] = useState({
    phase: "consultation",
    title: "",
    status: "planned",
    event_datetime_local: fmtDateTimeLocal(new Date().toISOString()),
  });

  const handleJourneyFormChange = (event) => {
    const { name, value } = event.target;
    setJourneyForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveJourneyItem = async () => {
    if (!canEdit || !patientId || !selectedTreatmentId) return;

    const title = (journeyForm.title || "").trim();
    if (!title) return;

    setJourneySaving(true);

    const payload = {
      course: Number(selectedTreatmentId),
      phase: journeyForm.phase,
      area: "clinical",
      title,
      status: journeyForm.status,
      event_datetime: toIsoFromLocalInput(journeyForm.event_datetime_local) || new Date().toISOString(),
      document_ids: [],
    };

    const result = await dispatch(createPatientJourneyItem({ patientId, data: payload }));
    setJourneySaving(false);

    if (createPatientJourneyItem.fulfilled.match(result)) {
      setJourneyForm((prev) => ({
        ...prev,
        title: "",
        status: "planned",
        event_datetime_local: fmtDateTimeLocal(new Date().toISOString()),
      }));
    }
  };

  // Editar hito
  const [journeyItemToEdit, setJourneyItemToEdit] = useState(null);
  const [editJourneySaving, setEditJourneySaving] = useState(false);
  const [editJourneyError, setEditJourneyError] = useState(null);
  const [editJourneyForm, setEditJourneyForm] = useState({
    phase: "consultation",
    title: "",
    status: "planned",
    event_datetime_local: "",
  });

  const openEditJourneyModal = (item) => {
    if (!canEdit) return;
    setEditJourneyError(null);
    setJourneyItemToEdit(item);
    setEditJourneyForm({
      phase: item?.phase || "consultation",
      title: item?.title || "",
      status: item?.status || "planned",
      event_datetime_local: fmtDateTimeLocal(item?.event_datetime || item?.created_at || new Date().toISOString()),
    });
    setEditJourneyOpen(true);
  };

  const closeEditJourneyModal = () => {
    if (editJourneySaving) return;
    setEditJourneyOpen(false);
    setJourneyItemToEdit(null);
    setEditJourneyError(null);
  };

  const handleEditJourneyFormChange = (event) => {
    const { name, value } = event.target;
    setEditJourneyForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveEditJourneyModal = async () => {
    if (!canEdit || !patientId || !journeyItemToEdit?.id) return;

    const title = (editJourneyForm.title || "").trim();
    if (!title) return;

    setEditJourneyError(null);
    setEditJourneySaving(true);

    const payload = {
      course: Number(selectedTreatmentId),
      phase: editJourneyForm.phase,
      area: "clinical",
      title,
      status: editJourneyForm.status,
      event_datetime: toIsoFromLocalInput(editJourneyForm.event_datetime_local) || new Date().toISOString(),
      document_ids: [],
    };

    const result = await dispatch(updatePatientJourneyItem({ id: journeyItemToEdit.id, data: payload }));
    setEditJourneySaving(false);

    if (updatePatientJourneyItem.fulfilled.match(result)) {
      closeEditJourneyModal();
      return;
    }

    setEditJourneyError(result.payload || "No se pudo actualizar el hito.");
  };

  // Eliminar hito
  const [journeyItemToDelete, setJourneyItemToDelete] = useState(null);
  const [deleteJourneySaving, setDeleteJourneySaving] = useState(false);
  const [deleteJourneyError, setDeleteJourneyError] = useState(null);

  const openDeleteJourneyModal = (item) => {
    if (!canEdit) return;
    setDeleteJourneyError(null);
    setJourneyItemToDelete(item);
    setDeleteJourneyOpen(true);
  };

  const closeDeleteJourneyModal = () => {
    if (deleteJourneySaving) return;
    setDeleteJourneyOpen(false);
    setJourneyItemToDelete(null);
    setDeleteJourneyError(null);
  };

  const confirmDeleteJourneyModal = async () => {
    if (!canEdit || !journeyItemToDelete?.id) return;

    setDeleteJourneyError(null);
    setDeleteJourneySaving(true);

    const result = await dispatch(deletePatientJourneyItem(journeyItemToDelete.id));
    setDeleteJourneySaving(false);

    if (deletePatientJourneyItem.fulfilled.match(result)) {
      closeDeleteJourneyModal();
      return;
    }

    setDeleteJourneyError(result.payload || "No se pudo eliminar el hito.");
  };

  /* =========================
     ESC + lock scroll (cuando hay modal)
  ========================= */

  useEffect(() => {
    const hasModal =
      createTreatmentOpen ||
      editTreatmentOpen ||
      deleteTreatmentOpen ||
      createSurgeryOpen ||
      deleteSurgeryOpen ||
      editJourneyOpen ||
      deleteJourneyOpen;

    if (!hasModal) return undefined;

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;

      if (deleteJourneyOpen) closeDeleteJourneyModal();
      else if (editJourneyOpen) closeEditJourneyModal();
      else if (deleteSurgeryOpen) closeDeleteSurgeryModal();
      else if (createSurgeryOpen) closeSurgeryModal();
      else if (deleteTreatmentOpen) closeDeleteTreatmentModal();
      else if (editTreatmentOpen) closeEditTreatmentModal();
      else if (createTreatmentOpen) closeCreateTreatmentModal();
    };

    document.addEventListener("keydown", handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    createTreatmentOpen,
    editTreatmentOpen,
    deleteTreatmentOpen,
    createSurgeryOpen,
    deleteSurgeryOpen,
    editJourneyOpen,
    deleteJourneyOpen,
  ]);

  /* =========================
     Render
  ========================= */

  return (
    <div className="pf-page-container">
      <div className="pf-header">
        <div>
          <h1>Seguimiento clínico</h1>
          <p>Tratamientos y línea del tiempo por hitos.</p>
        </div>

        {patientData && (
          <div className="pf-header-patient">
            <UserIcon size={18} />
            <span>{patientData.full_name || `Paciente #${patientData.id}`}</span>
          </div>
        )}
      </div>

      <div className="pf-layout">
        {/* =========================
            IZQUIERDA: TRATAMIENTOS
           ========================= */}
        <div className="pf-column pf-column--left">
          <section className="pf-card">
            <div className="pf-card-header">
              <div className="pf-card-title-wrap">
                <Activity size={18} />
                <div>
                  <h2 className="pf-card-title">Tratamientos</h2>
                  <p className="pf-card-sub">
                    Cada tratamiento agrupa cirugías e hitos del seguimiento.
                  </p>
                </div>
              </div>

              {canEdit && (
                <button type="button" className="btn-secondary btn-sm" onClick={openCreateTreatmentModal}>
                  <Plus size={16} />
                  Nuevo tratamiento
                </button>
              )}
            </div>

            <div className="pf-card-body">
              {treatments.length === 0 && (
                <div className="pf-empty">
                  <AlertCircle size={18} />
                  <div>
                    <div className="pf-empty-title">Aún no hay tratamientos.</div>
                    {canEdit && (
                      <div className="pf-empty-text">
                        Crea un tratamiento para agrupar cirugías y seguimiento.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {treatments.length > 0 && (
                <div className="pf-surgeries-list">
                  {treatments.map((treatment) => {
                    const isActive = String(treatment.id) === String(selectedTreatmentId);

                    const dateText = niceDate(
                      treatment.start_date || treatment.created_at || treatment.updated_at
                    );

                    const status = treatment.status || "active";
                    const statusText = treatmentStatusLabel(status);

                    const treatmentSurgeries = getTreatmentSurgeries(treatment.id);

                    return (
                      <button
                        key={treatment.id}
                        type="button"
                        className={`pf-surgery-card pf-treatment-card ${
                          isActive ? "pf-surgery-card--active" : ""
                        }`}
                        onClick={() => setSelectedTreatmentId(treatment.id)}
                      >
                        {canEdit && (
                          <div className="pf-treatment-card-actions">
                            <button
                              type="button"
                              className="pf-icon-button"
                              onClick={(event) => {
                                event.stopPropagation();
                                openCreateSurgeryModal(treatment.id);
                              }}
                              aria-label="Añadir cirugía"
                              title="Añadir cirugía"
                            >
                              <Plus size={14} />
                            </button>

                            <button
                              type="button"
                              className="pf-icon-button"
                              onClick={(event) => {
                                event.stopPropagation();
                                openEditTreatmentModal(treatment);
                              }}
                              aria-label="Editar tratamiento"
                              title="Editar tratamiento"
                            >
                              <Pencil size={14} />
                            </button>

                            <button
                              type="button"
                              className="pf-icon-button pf-icon-button--danger"
                              onClick={(event) => {
                                event.stopPropagation();
                                openDeleteTreatmentModal(treatment);
                              }}
                              aria-label="Eliminar tratamiento"
                              title="Eliminar tratamiento"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        )}

                        <div className="pf-surgery-main">
                          <div className="pf-surgery-title">Tratamiento</div>

                          <div className="pf-surgery-meta">
                            <span>
                              <CalendarDays size={14} />
                              {dateText}
                            </span>

                            <span className={`pf-surgery-status pf-surgery-status--${status}`}>
                              {statusText}
                            </span>
                          </div>

                          {isActive && (
                            <div className="pf-treatment-surgeries">
                              {treatmentSurgeries.length === 0 ? (
                                <div className="pf-empty pf-empty--soft">
                                  <AlertCircle size={18} />
                                  <div>
                                    <div className="pf-empty-title">
                                      Sin cirugías en este tratamiento.
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                treatmentSurgeries.map((surgery) => {
                                  const doctorObj = doctors.find(
                                    (d) => String(d.id) === String(surgery.surgeon)
                                  );
                                  const doctorLabel = doctorObj ? getDoctorLabel(doctorObj) : "—";
                                  const surgeryDateText = niceDate(surgery.date);

                                  return (
                                    <div
                                      key={surgery.id}
                                      className="pf-treatment-surgery-row"
                                      onClick={(e) => e.stopPropagation()}
                                      role="presentation"
                                    >
                                      <div className="pf-treatment-surgery-main">
                                        <div className="pf-treatment-surgery-title">
                                          {surgery.procedure_name || "Cirugía"}
                                        </div>
                                        <div className="pf-treatment-surgery-meta">
                                          <span>
                                            <CalendarDays size={14} />
                                            {surgeryDateText}
                                          </span>
                                          <span>
                                            <UserIcon size={14} />
                                            {doctorLabel}
                                          </span>
                                        </div>
                                      </div>

                                      {canEdit && (
                                        <div className="pf-item-actions">
                                          <button
                                            type="button"
                                            className="pf-icon-button"
                                            onClick={() => openEditSurgeryModal(treatment.id, surgery)}
                                            aria-label="Editar cirugía"
                                            title="Editar cirugía"
                                          >
                                            <Pencil size={14} />
                                          </button>

                                          <button
                                            type="button"
                                            className="pf-icon-button pf-icon-button--danger"
                                            onClick={() => openDeleteSurgeryModal(surgery)}
                                            aria-label="Eliminar cirugía"
                                            title="Eliminar cirugía"
                                          >
                                            <X size={14} />
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        </div>

        {/* =========================
            DERECHA: TIMELINE
           ========================= */}
        <div className="pf-column pf-column--right">
          {!selectedTreatmentId && (
            <section className="pf-card">
              <div className="pf-card-body">
                <div className="pf-empty">
                  <Activity size={18} />
                  <div>
                    <div className="pf-empty-title">Selecciona un tratamiento.</div>
                    <div className="pf-empty-text">
                      La línea del tiempo se guardará dentro del tratamiento.
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {selectedTreatmentId && (
            <section className="pf-card">
              <div className="pf-card-header">
                <div className="pf-card-title-wrap">
                  <Clock size={18} />
                  <div>
                    <h2 className="pf-card-title">Línea del tiempo</h2>
                    <p className="pf-card-sub">Fase + hito, con fecha y estado.</p>
                  </div>
                </div>

                {selectedTreatment && (
                  <div className="pf-surgery-header-info">
                    <div className="pf-surgery-header-title">
                      {niceDate(selectedTreatment.start_date || selectedTreatment.created_at)}
                    </div>
                    <div className="pf-surgery-header-meta">
                      <span
                        className={`pf-surgery-status pf-surgery-status--${
                          selectedTreatment.status || "active"
                        }`}
                      >
                        {treatmentStatusLabel(selectedTreatment.status)}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="pf-card-body">
                {canEdit && (
                  <div className="pf-journey-simple-form">
                    <div className="pf-field">
                      <label>Fase</label>
                      <select
                        className="pf-input"
                        name="phase"
                        value={journeyForm.phase}
                        onChange={handleJourneyFormChange}
                      >
                        {JOURNEY_PHASES.map((phase) => (
                          <option key={phase.key} value={phase.key}>
                            {phase.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="pf-field">
                      <label>Estado</label>
                      <select
                        className="pf-input"
                        name="status"
                        value={journeyForm.status}
                        onChange={handleJourneyFormChange}
                      >
                        {JOURNEY_STATUS_OPTIONS.map((status) => (
                          <option key={status.value} value={status.value}>
                            {status.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="pf-field pf-field--full">
                      <label>Hito</label>
                      <input
                        className="pf-input"
                        type="text"
                        name="title"
                        value={journeyForm.title}
                        onChange={handleJourneyFormChange}
                        placeholder='Ej. "Cámara hiperbárica", "Analítica", "Presupuesto pagado"...'
                      />
                    </div>

                    <div className="pf-field pf-field--full">
                      <label>Fecha y hora</label>
                      <input
                        className="pf-input"
                        type="datetime-local"
                        name="event_datetime_local"
                        value={journeyForm.event_datetime_local}
                        onChange={handleJourneyFormChange}
                      />
                    </div>

                    <div className="pf-actions pf-actions--right pf-field--full">
                      <button
                        type="button"
                        className="btn-primary btn-sm"
                        onClick={saveJourneyItem}
                        disabled={journeySaving || !journeyForm.title.trim()}
                      >
                        {journeySaving ? "Guardando…" : "Añadir hito"}
                      </button>
                    </div>
                  </div>
                )}

                {journeyTimeline.length === 0 && (
                  <div className="pf-empty pf-empty--soft">
                    <Clock size={18} />
                    <div>
                      <div className="pf-empty-title">Aún no hay hitos.</div>
                      {canEdit && <div className="pf-empty-text">Añade el primero arriba.</div>}
                    </div>
                  </div>
                )}

                {journeyTimeline.length > 0 && (
                  <div className="pf-timeline-scroll">
                    <ul className="pf-timeline pf-timeline--journey">
                      {journeyTimeline.map((item, index) => {
                        const whenText = niceDate(item.event_datetime || item.created_at);
                        const isDone = item.status === "done";
                        const isLast = index === journeyTimeline.length - 1;

                        return (
                          <li key={item.id} className="pf-timeline-item">
                            <div className="pf-timeline-dot-column">
                              <div
                                className={`pf-timeline-dot ${
                                  isDone ? "pf-timeline-dot--done" : ""
                                }`}
                              />
                              {!isLast && <div className="pf-timeline-line" />}
                            </div>

                            <div className="pf-timeline-card">
                              <div className="pf-timeline-header">
                                <div className="pf-timeline-title-row">
                                  <div className="pf-timeline-title">
                                    {isDone ? <CheckCircle2 size={15} /> : <Clock size={15} />}
                                    <span>{item.title || "Hito sin título"}</span>
                                  </div>
                                  <div className="pf-timeline-date">{whenText}</div>
                                </div>

                                <div className="pf-journey-simple-meta">
                                  <span className="pf-journey-simple-chip">{phaseLabel(item.phase)}</span>
                                  <span
                                    className={`pf-journey-simple-chip pf-journey-simple-chip--${
                                      item.status || "planned"
                                    }`}
                                  >
                                    {statusLabel(item.status)}
                                  </span>
                                </div>
                              </div>

                              {canEdit && (
                                <div className="pf-timeline-footer">
                                  <div className="pf-item-actions">
                                    <button
                                      type="button"
                                      className="pf-icon-button"
                                      onClick={() => openEditJourneyModal(item)}
                                      aria-label="Editar hito"
                                      title="Editar hito"
                                    >
                                      <Pencil size={14} />
                                    </button>
                                    <button
                                      type="button"
                                      className="pf-icon-button pf-icon-button--danger"
                                      onClick={() => openDeleteJourneyModal(item)}
                                      aria-label="Eliminar hito"
                                      title="Eliminar hito"
                                    >
                                      <X size={14} />
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            </section>
          )}
        </div>
      </div>

      {/* =========================
          MODAL: CREAR TRATAMIENTO
         ========================= */}
      {createTreatmentOpen && (
        <div className="ui-modal-overlay" onClick={closeCreateTreatmentModal} role="presentation">
          <div
            className="ui-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Crear tratamiento"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ui-modal-header">
              <h3>Nuevo tratamiento</h3>

              <button
                type="button"
                className="ghost btn-xs"
                onClick={closeCreateTreatmentModal}
                disabled={createTreatmentSaving}
                aria-label="Cerrar"
                title="Cerrar"
              >
                <X width={16} height={16} aria-hidden="true" />
              </button>
            </div>

            <div className="ui-modal-body">
              <div className="ui-field">
                <div className="ui-label">Fecha de inicio</div>
                <input
                  className="ui-input"
                  type="date"
                  name="start_date"
                  value={createTreatmentForm.start_date}
                  onChange={handleCreateTreatmentChange}
                  disabled={createTreatmentSaving}
                />
              </div>

              <div className="ui-field">
                <div className="ui-label">Estado</div>
                <select
                  className="ui-input"
                  name="status"
                  value={createTreatmentForm.status}
                  onChange={handleCreateTreatmentChange}
                  disabled={createTreatmentSaving}
                >
                  {TREATMENT_STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ui-field">
                <div className="ui-label">Descripción (opcional)</div>
                <textarea
                  className="ui-input"
                  name="description"
                  rows={3}
                  value={createTreatmentForm.description}
                  onChange={handleCreateTreatmentChange}
                  disabled={createTreatmentSaving}
                  placeholder="Notas generales del tratamiento…"
                />
              </div>

              {createTreatmentError ? <div className="ui-modal-error">{createTreatmentError}</div> : null}
            </div>

            <div className="ui-modal-footer">
              <button
                type="button"
                className="ghost btn-sm"
                onClick={closeCreateTreatmentModal}
                disabled={createTreatmentSaving}
              >
                Cancelar
              </button>

              <button
                type="button"
                className="btn-primary btn-sm"
                onClick={saveCreateTreatment}
                disabled={createTreatmentSaving}
              >
                {createTreatmentSaving ? "Creando…" : "Crear tratamiento"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================
          MODAL: EDITAR TRATAMIENTO
         ========================= */}
      {editTreatmentOpen && (
        <div className="ui-modal-overlay" onClick={closeEditTreatmentModal} role="presentation">
          <div
            className="ui-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Editar tratamiento"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ui-modal-header">
              <h3>Editar tratamiento</h3>

              <button
                type="button"
                className="ghost btn-xs"
                onClick={closeEditTreatmentModal}
                disabled={editTreatmentSaving}
                aria-label="Cerrar"
                title="Cerrar"
              >
                <X width={16} height={16} aria-hidden="true" />
              </button>
            </div>

            <div className="ui-modal-body">
              <div className="ui-field">
                <div className="ui-label">Fecha de inicio</div>
                <input
                  className="ui-input"
                  type="date"
                  name="start_date"
                  value={editTreatmentForm.start_date}
                  onChange={handleEditTreatmentChange}
                  disabled={editTreatmentSaving}
                />
              </div>

              <div className="ui-field">
                <div className="ui-label">Fecha de fin (opcional)</div>
                <input
                  className="ui-input"
                  type="date"
                  name="end_date"
                  value={editTreatmentForm.end_date}
                  onChange={handleEditTreatmentChange}
                  disabled={editTreatmentSaving}
                />
              </div>

              <div className="ui-field">
                <div className="ui-label">Estado</div>
                <select
                  className="ui-input"
                  name="status"
                  value={editTreatmentForm.status}
                  onChange={handleEditTreatmentChange}
                  disabled={editTreatmentSaving}
                >
                  {TREATMENT_STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ui-field">
                <div className="ui-label">Descripción</div>
                <textarea
                  className="ui-input"
                  name="description"
                  rows={3}
                  value={editTreatmentForm.description}
                  onChange={handleEditTreatmentChange}
                  disabled={editTreatmentSaving}
                  placeholder="Notas generales del tratamiento…"
                />
              </div>

              {editTreatmentError ? <div className="ui-modal-error">{editTreatmentError}</div> : null}
            </div>

            <div className="ui-modal-footer">
              <button
                type="button"
                className="ghost btn-sm"
                onClick={closeEditTreatmentModal}
                disabled={editTreatmentSaving}
              >
                Cancelar
              </button>

              <button
                type="button"
                className="btn-primary btn-sm"
                onClick={saveEditTreatment}
                disabled={editTreatmentSaving}
              >
                {editTreatmentSaving ? "Guardando…" : "Guardar cambios"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================
          MODAL: ELIMINAR TRATAMIENTO
         ========================= */}
      {deleteTreatmentOpen && (
        <div className="ui-modal-overlay" onClick={closeDeleteTreatmentModal} role="presentation">
          <div
            className="ui-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Eliminar tratamiento"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ui-modal-header">
              <h3>Eliminar tratamiento</h3>

              <button
                type="button"
                className="ghost btn-xs"
                onClick={closeDeleteTreatmentModal}
                disabled={deleteTreatmentSaving}
                aria-label="Cerrar"
                title="Cerrar"
              >
                <X width={16} height={16} aria-hidden="true" />
              </button>
            </div>

            <div className="ui-modal-body ui-modal-body--single">
              <div className="ui-delete-help">
                ¿Seguro que quieres eliminar este tratamiento?
                <div className="ui-delete-subhelp">
                  Fecha: {niceDate(treatmentToDelete?.start_date || treatmentToDelete?.created_at)} · Estado:{" "}
                  {treatmentStatusLabel(treatmentToDelete?.status)}
                </div>
              </div>

              {deleteTreatmentError ? <div className="ui-modal-error">{deleteTreatmentError}</div> : null}
            </div>

            <div className="ui-modal-footer">
              <button
                type="button"
                className="ghost btn-sm"
                onClick={closeDeleteTreatmentModal}
                disabled={deleteTreatmentSaving}
              >
                Cancelar
              </button>

              <button
                type="button"
                className="btn-danger btn-sm"
                onClick={confirmDeleteTreatment}
                disabled={deleteTreatmentSaving}
              >
                {deleteTreatmentSaving ? "Eliminando…" : "Eliminar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================
          MODAL: CREAR/EDITAR CIRUGÍA
         ========================= */}
      {createSurgeryOpen && (
        <div className="ui-modal-overlay" onClick={closeSurgeryModal} role="presentation">
          <div
            className="ui-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Cirugía"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ui-modal-header">
              <h3>{surgeryToEdit ? "Editar cirugía" : "Nueva cirugía"}</h3>

              <button
                type="button"
                className="ghost btn-xs"
                onClick={closeSurgeryModal}
                disabled={surgeryModalSaving}
                aria-label="Cerrar"
                title="Cerrar"
              >
                <X width={16} height={16} aria-hidden="true" />
              </button>
            </div>

            <div className="ui-modal-body">
              <div className="ui-field">
                <div className="ui-label">Procedimiento</div>
                <input
                  className="ui-input"
                  type="text"
                  name="procedure_name"
                  value={surgeryForm.procedure_name}
                  onChange={handleSurgeryFormChange}
                  disabled={surgeryModalSaving}
                  placeholder="Ej. Rinoplastia"
                />
              </div>

              <div className="ui-field">
                <div className="ui-label">Fecha</div>
                <input
                  className="ui-input"
                  type="date"
                  name="date"
                  value={surgeryForm.date}
                  onChange={handleSurgeryFormChange}
                  disabled={surgeryModalSaving}
                />
              </div>

              <div className="ui-field">
                <div className="ui-label">Médico responsable</div>
                <select
                  className="ui-input"
                  name="surgeon"
                  value={surgeryForm.surgeon}
                  onChange={handleSurgeryFormChange}
                  disabled={surgeryModalSaving}
                >
                  <option value="">Selecciona médico</option>
                  {doctors.map((doctor) => (
                    <option key={doctor.id} value={doctor.id}>
                      {getDoctorLabel(doctor)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ui-field">
                <div className="ui-label">Notas (opcional)</div>
                <textarea
                  className="ui-input"
                  name="notes"
                  rows={3}
                  value={surgeryForm.notes}
                  onChange={handleSurgeryFormChange}
                  disabled={surgeryModalSaving}
                  placeholder="Detalles clínicos relevantes…"
                />
              </div>

              {surgeryModalError ? <div className="ui-modal-error">{surgeryModalError}</div> : null}
            </div>

            <div className="ui-modal-footer">
              <button
                type="button"
                className="ghost btn-sm"
                onClick={closeSurgeryModal}
                disabled={surgeryModalSaving}
              >
                Cancelar
              </button>

              <button
                type="button"
                className="btn-primary btn-sm"
                onClick={saveSurgeryModal}
                disabled={surgeryModalSaving}
              >
                {surgeryModalSaving ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================
          MODAL: ELIMINAR CIRUGÍA
         ========================= */}
      {deleteSurgeryOpen && (
        <div className="ui-modal-overlay" onClick={closeDeleteSurgeryModal} role="presentation">
          <div
            className="ui-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Eliminar cirugía"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ui-modal-header">
              <h3>Eliminar cirugía</h3>

              <button
                type="button"
                className="ghost btn-xs"
                onClick={closeDeleteSurgeryModal}
                disabled={deleteSurgerySaving}
                aria-label="Cerrar"
                title="Cerrar"
              >
                <X width={16} height={16} aria-hidden="true" />
              </button>
            </div>

            <div className="ui-modal-body ui-modal-body--single">
              <div className="ui-delete-help">
                ¿Seguro que quieres eliminar la cirugía{" "}
                <strong>{surgeryToDelete?.procedure_name || "sin nombre"}</strong>?
                <div className="ui-delete-subhelp">
                  Fecha: {niceDate(surgeryToDelete?.date)} · Médico:{" "}
                  {(() => {
                    const doctorObj = doctors.find(
                      (d) => String(d.id) === String(surgeryToDelete?.surgeon)
                    );
                    return doctorObj ? getDoctorLabel(doctorObj) : "—";
                  })()}
                </div>
              </div>

              {deleteSurgeryError ? <div className="ui-modal-error">{deleteSurgeryError}</div> : null}
            </div>

            <div className="ui-modal-footer">
              <button
                type="button"
                className="ghost btn-sm"
                onClick={closeDeleteSurgeryModal}
                disabled={deleteSurgerySaving}
              >
                Cancelar
              </button>

              <button
                type="button"
                className="btn-danger btn-sm"
                onClick={confirmDeleteSurgery}
                disabled={deleteSurgerySaving}
              >
                {deleteSurgerySaving ? "Eliminando…" : "Eliminar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================
          MODAL EDITAR HITO
         ========================= */}
      {editJourneyOpen && (
        <div className="ui-modal-overlay" onClick={closeEditJourneyModal} role="presentation">
          <div
            className="ui-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Editar hito"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ui-modal-header">
              <h3>Editar hito</h3>

              <button
                type="button"
                className="ghost btn-xs"
                onClick={closeEditJourneyModal}
                disabled={editJourneySaving}
                aria-label="Cerrar"
                title="Cerrar"
              >
                <X width={16} height={16} aria-hidden="true" />
              </button>
            </div>

            <div className="ui-modal-body">
              <div className="ui-field">
                <div className="ui-label">Fase</div>
                <select
                  className="ui-input"
                  name="phase"
                  value={editJourneyForm.phase}
                  onChange={handleEditJourneyFormChange}
                  disabled={editJourneySaving}
                >
                  {JOURNEY_PHASES.map((phase) => (
                    <option key={phase.key} value={phase.key}>
                      {phase.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ui-field">
                <div className="ui-label">Estado</div>
                <select
                  className="ui-input"
                  name="status"
                  value={editJourneyForm.status}
                  onChange={handleEditJourneyFormChange}
                  disabled={editJourneySaving}
                >
                  {JOURNEY_STATUS_OPTIONS.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ui-field">
                <div className="ui-label">Hito</div>
                <input
                  className="ui-input"
                  type="text"
                  name="title"
                  value={editJourneyForm.title}
                  onChange={handleEditJourneyFormChange}
                  disabled={editJourneySaving}
                  placeholder='Ej. "Cámara hiperbárica"'
                />
                <div className="ui-help">Texto corto y claro (una acción/hito).</div>
              </div>

              <div className="ui-field">
                <div className="ui-label">Fecha y hora</div>
                <input
                  className="ui-input"
                  type="datetime-local"
                  name="event_datetime_local"
                  value={editJourneyForm.event_datetime_local}
                  onChange={handleEditJourneyFormChange}
                  disabled={editJourneySaving}
                />
              </div>

              {editJourneyError ? <div className="ui-modal-error">{editJourneyError}</div> : null}
            </div>

            <div className="ui-modal-footer">
              <button
                type="button"
                className="ghost btn-sm"
                onClick={closeEditJourneyModal}
                disabled={editJourneySaving}
              >
                Cancelar
              </button>

              <button
                type="button"
                className="btn-primary btn-sm"
                onClick={saveEditJourneyModal}
                disabled={editJourneySaving || !editJourneyForm.title.trim()}
              >
                {editJourneySaving ? "Guardando…" : "Guardar cambios"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================
          MODAL ELIMINAR HITO
         ========================= */}
      {deleteJourneyOpen && (
        <div className="ui-modal-overlay" onClick={closeDeleteJourneyModal} role="presentation">
          <div
            className="ui-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Eliminar hito"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ui-modal-header">
              <h3>Eliminar hito</h3>

              <button
                type="button"
                className="ghost btn-xs"
                onClick={closeDeleteJourneyModal}
                disabled={deleteJourneySaving}
                aria-label="Cerrar"
                title="Cerrar"
              >
                <X width={16} height={16} aria-hidden="true" />
              </button>
            </div>

            <div className="ui-modal-body ui-modal-body--single">
              <div className="ui-delete-help">
                ¿Seguro que quieres eliminar el hito{" "}
                <strong>{journeyItemToDelete?.title || "sin título"}</strong>?
                <div className="ui-delete-subhelp">
                  Fase: {phaseLabel(journeyItemToDelete?.phase)} · Estado:{" "}
                  {statusLabel(journeyItemToDelete?.status)} · Fecha:{" "}
                  {niceDate(journeyItemToDelete?.event_datetime || journeyItemToDelete?.created_at)}
                </div>
              </div>

              {deleteJourneyError ? <div className="ui-modal-error">{deleteJourneyError}</div> : null}
            </div>

            <div className="ui-modal-footer">
              <button
                type="button"
                className="ghost btn-sm"
                onClick={closeDeleteJourneyModal}
                disabled={deleteJourneySaving}
              >
                Cancelar
              </button>

              <button
                type="button"
                className="btn-danger btn-sm"
                onClick={confirmDeleteJourneyModal}
                disabled={deleteJourneySaving}
              >
                {deleteJourneySaving ? "Eliminando…" : "Eliminar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientFollowUp;